import * as THREE from 'three';
import { tileableFbm, worley2, clamp, smoothstep } from '../core/Rand.js';

/**
 * Weapon materials.
 *
 * Everything here is generated procedurally at boot — no network assets.
 *
 * ## Why this file was rewritten (iteration 2)
 *
 * The critique scored `wpn_closeup` materials 1/10: "a greybox at 30 cm from camera",
 * uniform roughness, incoherent metalness. The cause was *not* a missing texture — the
 * maps existed and were bound. It was that every structural material was authored as
 * **bare metal** (`metalness` 0.80–0.92). A metal has no diffuse albedo, so its entire
 * appearance is the environment convolved by its roughness; and `ctx.viewScene.environment`
 * is force-assigned every frame from `ctx.scene.environment` — a bright daylight sky —
 * by `ProbeRig.syncViewmodelLights` (and, previously, by our own `_syncWorldLights`).
 * A rough metal reflecting a near-uniform bright sky is *exactly* a flat mid-grey with
 * no albedo variation. Every albedo byte we authored was multiplied by (1 − metalness).
 *
 * The fix is physical, not a hack: a phosphated, anodised or Cerakoted firearm surface
 * is a **conversion coating / oxide layer — a dielectric**. Only genuinely bare metal
 * (muzzle crown, bolt face, extractor, worn edges) is `metalness = 1`. That single
 * change makes albedo, roughness zoning and edge wear all visible at viewmodel range,
 * and it is what makes the metalness *coherent* — the receiver flat now reads dielectric
 * and only the rubbed edges read as steel.
 *
 * ## Why this file was rewritten again (iteration 3) — and what changed about
 * ## `specularIntensity`
 *
 * Iteration 2's physics was right and is kept, but it left the weapon a single
 * colour: every structural family was authored at a near-BLACK, near-NEUTRAL
 * albedo (steel [0.102,0.105,0.099], anodise [0.088,0.093,0.101], polymer
 * [0.078,0.083,0.091]) and `specularIntensity` was pulled down to 0.45-0.62 so
 * that near-black diffuse could be seen at all. Measured on the shipped frame,
 * six components of `wpn_closeup` spanned **2.1 degrees of hue** — because with
 * an achromatic albedo the only thing colouring the gun was the golden-hour key,
 * and one light gives one hue.
 *
 * The albedo is now the materials team's differentiated palette (see FAMILY), so
 * each part carries a hue of its own — and with a real albedo to reflect there is
 * no longer any reason to suppress the specular. `specularIntensity` is back at
 * 0.62-1.0, which is also where the other half of FINDINGS F13 comes from: the
 * build had no bare metal anywhere, so nothing on a hero asset 30 cm from the
 * camera returned a specular at all.
 *
 * ## Sampler budget (FINDINGS F6)
 *
 * The fragment stage has 16 texture units and the scene's shadow maps eat most of them;
 * anything above ~4 samplers per material fails to link. Every gun material therefore
 * binds exactly four: `map`, `normalMap`, one **packed ORM** map, and the environment.
 * The ORM map is sampled by our own uniform and unpacked in the shader patch below —
 * three.js would otherwise spend one unit each on `roughnessMap`, `metalnessMap` and
 * `aoMap` even when they are the same texture object.
 *
 * ## Vertex channels consumed by the shader patch
 *
 *   aWear   0..1  chamfer / rub polish -> bare metal, low roughness
 *   aGrime  0..1  carbon fouling and powder residue -> dark, matte, dielectric
 *   aAO     0..1  baked cavity occlusion (1 = open)
 */

const TAU = Math.PI * 2;

/* ------------------------------------------------------------------ *
 * Procedural texture generation
 * ------------------------------------------------------------------ */

function dataTex(size, fill, { srgb = false, aniso = 8 } = {}) {
  const data = new Uint8Array(size * size * 4);
  fill(data, size);
  const t = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.magFilter = THREE.LinearFilter;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.generateMipmaps = true;
  t.anisotropy = aniso;
  t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  t.needsUpdate = true;
  return t;
}

/** Height field -> tangent-space normal map (Sobel), wrapping at the edges. */
function normalFromHeight(size, heightAt, strength = 1) {
  return dataTex(size, (data) => {
    const h = new Float32Array(size * size);
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) h[y * size + x] = heightAt(x / size, y / size);
    }
    const at = (x, y) => h[((y + size) % size) * size + ((x + size) % size)];
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const dx = (at(x + 1, y - 1) + 2 * at(x + 1, y) + at(x + 1, y + 1))
          - (at(x - 1, y - 1) + 2 * at(x - 1, y) + at(x - 1, y + 1));
        const dy = (at(x - 1, y + 1) + 2 * at(x, y + 1) + at(x + 1, y + 1))
          - (at(x - 1, y - 1) + 2 * at(x, y - 1) + at(x + 1, y - 1));
        const nx = -dx * strength, ny = -dy * strength, nz = 1;
        const l = Math.hypot(nx, ny, nz);
        const i = (y * size + x) * 4;
        data[i] = ((nx / l) * 0.5 + 0.5) * 255;
        data[i + 1] = ((ny / l) * 0.5 + 0.5) * 255;
        data[i + 2] = ((nz / l) * 0.5 + 0.5) * 255;
        data[i + 3] = 255;
      }
    }
  });
}

/** Pack three linear fields into one texture: R = roughness, G = metalness, B = AO. */
function ormFromFields(size, fn) {
  return dataTex(size, (data) => {
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const [r, g, b] = fn(x / size, y / size);
        const i = (y * size + x) * 4;
        data[i] = clamp(r, 0, 1) * 255;
        data[i + 1] = clamp(g, 0, 1) * 255;
        data[i + 2] = clamp(b, 0, 1) * 255;
        data[i + 3] = 255;
      }
    }
  });
}

/** sRGB albedo from a per-texel linear-ish colour function. */
function albedoFromFields(size, fn) {
  return dataTex(size, (data) => {
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const c = fn(x / size, y / size);
        const i = (y * size + x) * 4;
        data[i] = clamp(c[0], 0, 1) * 255;
        data[i + 1] = clamp(c[1], 0, 1) * 255;
        data[i + 2] = clamp(c[2], 0, 1) * 255;
        data[i + 3] = 255;
      }
    }
  }, { srgb: true });
}

const tint = (base, k) => [base[0] * k, base[1] * k, base[2] * k];

/**
 * A field of fine directional scratches, tileable.
 *
 * Measured against the shipped rifle in `refs/mw3_04.jpg`, our weapon carried
 * a third of its local luminance variation (std 0.026 against 0.087). Most of
 * what a real receiver flat has that ours did not is HANDLING: hairline draw
 * marks, rack rash, and the fine random scoring a weapon picks up in a bag.
 * Those are high-contrast, high-frequency and directional, which is exactly the
 * energy band a broach grain plus a slow macro field leaves empty.
 *
 * Stretching one axis by an INTEGER factor keeps the field tileable on the
 * torus (a non-integer stretch puts a seam down the middle of every part), and
 * thresholding an fbm near its top gives thin, sparse, unevenly-lengthed lines
 * rather than a corduroy.
 */
/* NB the stretch about the midpoint. `tileableFbm` is an averaged Perlin sum and
 * lands in roughly +-0.4, so `n * 0.5 + 0.5` only ever spans about 0.30..0.70 and
 * clusters hard on 0.5. Thresholding that at 0.70 produces EXACTLY ZERO scratches
 * everywhere, which is what the first version of this did — the same trap the skin
 * zoning fell into and documented in Materials.js, re-entered from another file. */
const scratches = (u, v, freq, stretch, thresh, seed = 0) => {
  const raw = tileableFbm(u + seed, v * stretch, freq, 2, 0.5) * 0.5 + 0.5;
  const n = clamp((raw - 0.5) * 2.6 + 0.5, 0, 1);
  return smoothstep(thresh, thresh + 0.10, n);
};

/**
 * Dust and dried oil film. Mid-frequency, patchy, and the single biggest source
 * of "this object exists in a place" on a close-up hard surface: it is what
 * stops a machined flat reading as a rendered rectangle.
 */
const filmField = (u, v, freq, seed = 0) => {
  const raw = tileableFbm(u + seed, v + seed * 0.7, freq, 4, 0.55) * 0.5 + 0.5;
  return smoothstep(0.34, 0.80, clamp((raw - 0.5) * 2.4 + 0.5, 0, 1));
};

/** hex (sRGB) -> the 0..1 sRGB triple the albedo generators multiply. */
const rgb = (hex) => [((hex >> 16) & 255) / 255, ((hex >> 8) & 255) / 255, (hex & 255) / 255];

/* ==================================================================== *
 * PALETTE — re-derived from the materials team's differentiated set
 *
 * The reviewer measured nine components of `wpn_closeup` and found 3.6 deg of
 * hue, 0.085 of saturation and 0.12 of luminance across six parts that are, in
 * reality, three unrelated materials. My own like-for-like re-measurement of
 * the shipped frame was tighter still: **2.1 deg of hue spread** across the six
 * sampled components (optic 23.4, receiver 21.3, handguard 21.8, stock 22.5,
 * magwell 22.9, barrel nut 22.5). Every structural family here was authored at
 * a near-black, near-neutral albedo — steel [0.102,0.105,0.099], anodise
 * [0.088,0.093,0.101], polymer [0.078,0.083,0.091] — so nothing on the weapon
 * carried a hue of its own and every pixel took the hue of the golden-hour key.
 * That is the whole defect: **the light was the only thing colouring the gun.**
 *
 * The materials team built and measured a differentiated set for exactly this
 * (`src/world/Materials.js` :: gun_*). Their six-sphere, one-light measurement:
 *
 *   gun_optic 22.5 | gun_polymer 23.6 | gun_polymer_grey 26.1
 *   gun_polymer_fde 28.9 | gun_steel_bare 30.0 | gun_alloy 44.7
 *   = 22.2 deg of hue, 0.382 of luminance, saturation 0.04-0.59, four finishes.
 *
 * WHY THIS FILE RE-DERIVES THEM RATHER THAN CLONING THEM.
 * `ctx.materials.get('gun_alloy')` is a 0.26 m-tile *world* material: its maps
 * are authored for a 26 cm span, while the viewmodel's triplanar UVs run at 64
 * tiles/metre (1.6 cm). Every feature in it would land at ~1/17 of its intended
 * size and mip straight back to the flat grey this fix exists to remove; and
 * `mapsFor()` would spend fresh texture-factory budget (FINDINGS: 592 MB of 640
 * already used, and going over silently HALVES the resolution of whatever is
 * generated next — which, at weapons-init time, is the LEVEL). So what is
 * consumed is their *authoring*: base colour, roughness band, metalness and IBL
 * weight, part by part, by the names they reported, regenerated here at
 * viewmodel texel density. Each family below names its source.
 * ==================================================================== */

/* ==================================================================== *
 * ITERATION 4 — the hue axis was the wrong axis (FINDINGS F15)
 *
 * Iteration 3 hit an acceptance criterion of ">= 20 deg hue spread" and took
 * the six sampled components from 1.5 deg to 28.1 deg. The metric passed and
 * the frame got worse: an olive upper (0x505e3c, hue 80), a graphite-blue
 * lower (0x31363f, hue 213), a saturated tan handguard (0x8f6f42, sat 0.54)
 * and a cool bright bare metal (0xb9bfc7) read as four unrelated PLASTICS.
 *
 * A real anodised-receiver / polymer-furniture rifle separates by **value and
 * finish inside a narrow hue band**. Two anodising lots differ by a stop of
 * value and by gloss, not by 80 degrees of hue; an FDE handguard against a
 * black upper is a VALUE contrast that happens to carry a little warmth.
 *
 * So the differentiation this file exists to provide is unchanged in amount
 * and moved onto the axes that carry it in reality:
 *
 *   VALUE  ladder, albedo max-channel:
 *     optic 0.102 < polymer 0.114 = steel 0.114 < metal 0.129
 *           < alloy 0.180 < polymer_grey 0.259 < polymer_fde 0.345 < bare 0.545
 *
 *   FINISH ladder, authored roughness band (specular lobe width). The bands were
 *   widened ~70 % in iteration 5: measured against the shipped rifle, our local
 *   luminance standard deviation was 0.026 against 0.087, i.e. a third of the
 *   detail energy, and a 0.14-wide roughness band cannot write structure into a
 *   specular lobe however good the map under it is. The ladder is unchanged — the
 *   bands still do not overlap at their centres — it is the SPREAD inside each
 *   part that grew, which is the whole of FINDINGS F20's second gap:
 *     bare 0.145-0.445 | optic 0.175-0.400 | alloy 0.235-0.505
 *     metal 0.330-0.645 | polymer_grey 0.400-0.690 | polymer 0.440-0.730
 *     polymer_fde 0.475-0.775 | steel 0.560-0.900 | rubber 0.860-0.985
 *
 *   HUE    the six sampled components land inside a single ~9 deg band on the
 *          rendered frame, which is the thing the criterion and the eye both
 *          read. Getting there means the AUTHORED albedo is pre-corrected for
 *          the key, not neutral — see below.
 *
 * ## Why the coatings are authored COOL and the furniture warm
 *
 * The viewmodel key is `sun.color` at golden hour, measured live as linear
 * (1, 0.60, 0.42) once the grade has had it — a 25 %-saturated orange at a
 * 45:1 key-to-ambient ratio. Multiply a NEUTRAL albedo by that and the part
 * renders at saturation 0.26-0.29: an orange-tan lump, which is precisely the
 * frame the iteration-3 palette produced with olive and graphite albedos and
 * precisely the frame a naive "just author it neutral grey" palette produces
 * too. Both were tried here and measured.
 *
 * So the anodised and phosphated coatings are authored on the far side of
 * neutral — linear channel ratios about 1 : 1.25 : 1.55 — which is also what
 * hard anodising and manganese phosphate genuinely are (blue-grey oxide
 * layers). They render as a *neutral gunmetal under a warm light*, at
 * saturation ~0.15, which is what a rifle looks like in a photograph.
 *
 * `polymer_fde` is the one part authored warm, because FDE furniture IS tan;
 * it is where the rendered hue band's warm end comes from, and against the
 * dark upper it reads mostly as a VALUE step (0.345 against 0.180) with a
 * little warmth on top — which is the reference behaviour F15 asked for.
 *
 * The upper and the lower are still two different anodising lots — but now
 * they differ the way two lots differ: the upper is a lighter SATIN type-III,
 * the lower a darker SEMI-MATTE one. That is a finish contrast, and it
 * survives at 30 cm where a hue contrast reads as paint.
 *
 * ## Why `ibl` and `spec` came down with the albedo
 *
 * `ProbeRig.syncViewmodelLights` (render's file, and the documented owner of
 * this) drives the viewmodel key at `sun.intensity * VM_KEY_SHADE` — measured
 * live at **15.4 against 0.34 of total ambient**, i.e. a 45:1 key. Under a key
 * that hard, an IBL/specular floor is very nearly albedo-independent, and the
 * first attempt at this palette proved it: dropping the optic's albedo by 35 %
 * left its rendered value UNCHANGED, because the floor was carrying it. That
 * is FINDINGS' "adding a constant to a region deletes its contrast" on a
 * viewmodel. The floor is now scaled with the coating it sits on, so the value
 * ladder above actually reaches the frame. `bare` keeps its full 1.55 IBL —
 * it is the one surface whose appearance IS the environment, and it is where
 * the F13 p99 comes from.
 * ==================================================================== */

/* ====================================================================== *
 * WHERE THE TAN COMES FROM, measured rather than argued
 *
 * The weapon renders at HSV saturation 0.295 against the shipped rifle's 0.138,
 * and it is NOT the albedo: `alloy` is authored at a linear blue:red ratio of
 * 1.63, which under the measured key of linear (1, 0.60, 0.42) predicts a
 * rendered saturation of about 0.16. The extra chroma is the DIRECT SPECULAR.
 *
 * A dielectric coating's F0 is white, so its specular lobe returns the key's
 * colour undiluted at full saturation — and on an albedo this dark the specular
 * is most of what comes back. `ProbeRig.syncViewmodelLights` drives the
 * viewmodel at a measured 45:1 key-to-ambient, so there is almost no cool sky
 * in the mix to balance it. The world does not have this problem because the
 * world gets real sky fill; only the viewmodel is lit by one warm light.
 *
 * Two responses, and deliberately only two, because iteration 3 proved that
 * chasing a colour number on this asset produces a toy (FINDINGS F15):
 *
 *  1. MORE COOL SKY, not less warm specular. `ibl` goes up ~35 % across the
 *     coatings. This desaturates by ADDING a cool term rather than by removing
 *     a warm one, so it costs no specular energy (FINDINGS F13 exists because
 *     this build has none to spare) and it widens the value range instead of
 *     compressing it.
 *  2. A SMALL FURTHER COOL BIAS in the authored albedo, ~8 %. Small on purpose:
 *     the pre-correction is against a key ratio owned by another team, and a
 *     large correction here becomes a blue rifle the day that ratio changes.
 *
 * The rest of the gap is the key:ambient ratio itself, which is render's, and
 * is reported rather than worked around.
 * ====================================================================== */
const FAMILY = {
  /* ---- anodised aluminium: the receivers, the rails, the optic --------- *
   * Three anodising lots. They differ by VALUE and GLOSS, not by hue: an
   * upper is never olive and a lower is never blue. */
  alloy: {
    src: 'gun_alloy', cls: 'anod', color: 0x22282e,
    rough: [0.235, 0.505], metalScale: 0.16, ibl: 0.84, spec: 0.62, nrm: 0.74,
    macro: [0.12, 0.130],
  },
  metal: {
    src: 'gun_metal', cls: 'anod', color: 0x171c21,
    rough: [0.330, 0.645], metalScale: 0.13, ibl: 0.68, spec: 0.48, nrm: 0.88,
    macro: [0.13, 0.150],
  },
  optic: {
    src: 'gun_optic', cls: 'blast', color: 0x11161a,
    rough: [0.175, 0.400], metalScale: 0.15, ibl: 0.95, spec: 0.66, nrm: 0.72,
    macro: [0.09, 0.100],
  },
  /* ---- manganese-phosphate steel: small parts, other weapons ---------- *
   * The roughest coating on the weapon, and the warmest — parkerising is a
   * porous crystal that holds oil, so it goes grey-brown, never black. */
  steel: {
    src: '(parkerising, kept)', cls: 'phos', color: 0x181b1d,
    rough: [0.560, 0.900], metalScale: 0.22, ibl: 0.68, spec: 0.44, nrm: 0.92,
    macro: [0.15, 0.120],
  },
  /* ---- BARE METAL. The other half of the specular fix (F13). ---------- *
   * Barrel, bolt carrier, charging handle, forward assist, ejection-port
   * lip, sling swivels, pins, muzzle crown, trigger. The only true metal in
   * the file and the only thing on the weapon that returns a real specular.
   *
   * It read as NICKEL in iteration 3 — a cool 0xb9bfc7 F0 at one gloss level
   * over a near-uniform bright sky is the definition of chrome. Four things
   * separate photographed steel from chrome and all four are here now:
   *
   *  - a WARM, off-neutral F0 at gun-steel reflectance rather than at plating
   *    reflectance. Measured: the viewmodel environment sits at ~1.9 after
   *    exposure, so an F0 of 0.43 linear (the old 0xb9bfc7 / 0xb0aea9) renders
   *    every sky-facing flat at sRGB 250 — white, whatever else is done to it.
   *    0x8b8781 is 0.25 linear and prints ~185, which is a bright steel part in
   *    sunlight rather than a bleached one.
   *  - macro tone and a macro roughness field (see applySurface's macro tap),
   *    which is what the previous per-texel "wide roughness field" could not be
   *    at 64 tiles/metre.
   *  - handling stains and draw polish in the albedo.
   *  - sky-occluded INDIRECT SPECULAR, so parts that cannot see the sky do not
   *    return it.
   *
   * This is NOT the dimming the previous pass correctly refused: the bright
   * value moves onto `wearColor`, i.e. onto the polished chamfers, bolt face and
   * worn-through edges where a bright value physically belongs — which is where
   * the F13 p99 comes from and where it stays. Verified on the tight rectangle,
   * not assumed. */
  bare: {
    /* Roughness floor 0.105 -> 0.155. Measured on the shipped frame, the polished
     * cylinders on this weapon (the charging handle, the takedown pins) render at
     * sRGB 0.918 with saturation 0.097 — a white plastic cylinder, and the exact
     * "bare metal reads as nickel, not steel" note FINDINGS F15 left open. At 0.105
     * over a near-uniform bright sky a cylinder returns one clean mirror sweep with
     * no structure in it whatever the albedo says. 0.155 is still well inside the
     * band where a surface returns an IMAGE of the sky rather than an average of it,
     * so the F13 p99 the polish band exists to carry is unchanged; what changes is
     * that the lobe is now wide enough for the macro roughness field to write
     * structure into it. */
    src: 'gun_steel_bare', cls: 'bare', color: 0x847f79,
    rough: [0.145, 0.445], metalScale: 1.0, ibl: 1.55, spec: 1.0, nrm: 0.58,
    macro: [0.28, 0.230],
  },
  /* ---- polymer: three mouldings, one hue band, three values ----------- */
  polymer: {
    src: 'gun_polymer', cls: 'poly', color: 0x13181d,
    rough: [0.440, 0.730], metalScale: 0.0, ibl: 0.54, spec: 0.46, nrm: 1.10,
    macro: [0.11, 0.110],
  },
  polymer_fde: {
    /* Measured on the shipped frame the FDE handguard renders at saturation 0.32.
     * FDE furniture IS the warm end of the palette and must stay so — this is where
     * the rendered hue band's warm end comes from — but 0x585044 is HSV 0.227 in the
     * map and the warm key adds its own chroma on top. 0x534d46 is 0.146, which
     * lands the rendered part nearer the shipped rifle's own furniture. */
    src: 'gun_polymer_fde', cls: 'fde', color: 0x534d46,
    rough: [0.475, 0.775], metalScale: 0.0, ibl: 0.58, spec: 0.46, nrm: 1.06,
    macro: [0.17, 0.120],
  },
  polymer_grey: {
    src: 'gun_polymer_grey', cls: 'poly', color: 0x373d42,
    rough: [0.400, 0.690], metalScale: 0.0, ibl: 0.54, spec: 0.44, nrm: 1.08,
    macro: [0.13, 0.110],
  },
};

function buildTextures() {
  const T = {};

  /* ---------------------------------------------------------------- *
   * SURFACE CLASSES
   *
   * Five distinct normal characters, so "an identical stipple normal on
   * every part" cannot survive: broached anodising, bead-blasted anodising,
   * phosphate crystal, polished steel, moulded orange peel. The ORM's red
   * channel is now a NORMALISED 0..1 field — `applySurface` remaps it into
   * each family's authored roughness band, and authoring absolute roughness
   * here as well meant that remap ran twice.
   * ---------------------------------------------------------------- */

  /* 1. Type-III anodise over a broached extrusion — receiver, rails. */
  T.anodNormal = normalFromHeight(512, (u, v) => {
    const lines = tileableFbm(u, v * 0.05, 7.0, 3, 0.55) * 0.22
      + Math.sin(v * TAU * 148 + tileableFbm(u, v, 6, 3) * 5) * 0.016;
    const grain = tileableFbm(u, v, 52, 4, 0.5) * 0.18;
    const w = worley2(u * 24, v * 24, 24);
    const pit = smoothstep(0.07, 0.0, w.f1) * 0.32;
    // rack rash: two scratch families crossing at different angles
    const sc = scratches(u, v, 11, 9, 0.70, 0.31) * 0.26
      + scratches(v, u, 17, 7, 0.74, 0.63) * 0.16;
    return lines + grain - pit - sc * 0.34;
  }, 1.35);
  T.anodORM = ormFromFields(512, (u, v) => {
    const n = tileableFbm(u, v, 8, 4, 0.5);
    const fine = tileableFbm(u, v * 0.1, 40, 3, 0.5);
    const w = worley2(u * 24, v * 24, 24);
    /* A scratch through anodising exposes the aluminium under it: the coating is
     * an oxide, so a scratch is BRIGHTER, SMOOTHER and genuinely METAL — three
     * channels moving together, which is what makes it read as damage rather
     * than as a dark line drawn on the paint. */
    const sc = scratches(u, v, 11, 9, 0.70, 0.31) + scratches(v, u, 17, 7, 0.74, 0.63) * 0.7;
    const film = filmField(u, v, 6, 0.19);
    // anodising is an oxide: dielectric, except where the layer has worn through
    return [
      clamp(0.50 + n * 0.62 + fine * 0.34 + film * 0.30 - sc * 0.34, 0, 1),
      clamp(0.34 + smoothstep(0.80, 0.99, 1 - w.f1) * 0.66 + sc * 0.55, 0, 1),
      clamp(0.80 + n * 0.20 - film * 0.12, 0, 1),
    ];
  });

  /* 2. Bead-blasted black anodise — the optic housing. Finer, flatter,
   *    tighter: an optic body is glass-bead blasted, not broached. */
  T.blastNormal = normalFromHeight(512, (u, v) => {
    const w1 = worley2(u * 96, v * 96, 96);
    const w2 = worley2(u * 210, v * 210, 210);
    return (1 - w1.f1) * 0.30 + (1 - w2.f1) * 0.22 + tileableFbm(u, v, 64, 3) * 0.14;
  }, 0.85);
  T.blastORM = ormFromFields(512, (u, v) => {
    const w = worley2(u * 96, v * 96, 96);
    const n = tileableFbm(u, v, 10, 3, 0.5);
    const sc = scratches(u, v, 14, 8, 0.74, 0.47);
    const film = filmField(u, v, 7, 0.53);
    return [
      clamp(0.46 + (1 - w.f1) * 0.42 + n * 0.26 + film * 0.26 - sc * 0.30, 0, 1),
      clamp(0.22 + sc * 0.60, 0, 1),
      clamp(0.86 + n * 0.14 - film * 0.10, 0, 1),
    ];
  });

  /* 3. Manganese-phosphate crystal. */
  T.phosNormal = normalFromHeight(512, (u, v) => {
    const w1 = worley2(u * 58, v * 58, 58);
    const w2 = worley2(u * 152, v * 152, 152);
    return (1 - w1.f1) * 0.50 + (1 - w2.f1) * 0.28 + tileableFbm(u, v, 30, 3) * 0.20;
  }, 1.25);
  T.phosORM = ormFromFields(512, (u, v) => {
    const w = worley2(u * 58, v * 58, 58);
    const fine = tileableFbm(u, v, 44, 3, 0.5);
    const sc = scratches(u, v, 13, 8, 0.72, 0.11);
    const film = filmField(u, v, 5, 0.83);
    return [
      clamp(0.52 + (1 - w.f1) * 0.44 + fine * 0.30 + film * 0.22 - sc * 0.36, 0, 1),
      clamp(0.30 + smoothstep(0.72, 0.95, w.f1) * 0.70 + sc * 0.55, 0, 1),
      clamp(0.72 + w.f1 * 0.28 - film * 0.10, 0, 1),
    ];
  });

  /* 4. Bare, lightly-polished steel. Draw-marks only — anything coarser
   *    would scatter the one real specular return on the weapon away. */
  T.bareNormal = normalFromHeight(512, (u, v) => {
    const draw = Math.sin(v * TAU * 320 + tileableFbm(u, v, 8, 3) * 6) * 0.030
      + tileableFbm(u * 2.2, v * 0.06, 26, 3, 0.5) * 0.070;
    const w = worley2(u * 40, v * 40, 40);
    return draw - smoothstep(0.05, 0.0, w.f1) * 0.16;
  }, 0.55);
  T.bareORM = ormFromFields(512, (u, v) => {
    const n = tileableFbm(u, v, 12, 4, 0.5);
    // Two LOW-frequency terms, at 2.4 and 5.1 tiles across the map, so the
    // roughness varies over centimetres rather than over texels. A per-texel
    // roughness field averages back to one gloss level the moment it mips; a
    // macro one survives, and macro tone variation is the whole difference
    // between steel and chrome.
    const macro = tileableFbm(u + 0.70, v + 0.20, 2.4, 4, 0.58);
    const macro2 = tileableFbm(u + 0.13, v + 0.61, 5.1, 3, 0.55);
    const belt = tileableFbm(u * 1.6, v * 0.05, 30, 3, 0.5);   // draw direction
    const w = worley2(u * 18, v * 18, 18);
    const handling = smoothstep(0.30, 0.02, w.f1);             // dulled by handling
    return [clamp(0.34 + macro * 0.86 + macro2 * 0.44 + n * 0.30
      + belt * 0.34 + handling * 0.18, 0, 1),
    0.94 + n * 0.10, 0.90 + n * 0.10];
  });

  /* 5. Injection-moulded orange peel, and a coarser variant for the
   *    textured FDE handguard so the two polymers do not share a surface. */
  T.polyNormal = normalFromHeight(512, (u, v) => {
    const peel = tileableFbm(u, v, 34, 4, 0.55) * 0.58;
    const micro = tileableFbm(u, v, 110, 3, 0.5) * 0.24;
    const flow = Math.sin((u * 3.1 + tileableFbm(u, v, 4, 3) * 2.4) * TAU) * 0.045;
    return peel + micro + flow;
  }, 1.15);
  T.fdeNormal = normalFromHeight(512, (u, v) => {
    const peel = tileableFbm(u, v, 22, 4, 0.55) * 0.52;
    const w = worley2(u * 66, v * 66, 66);
    const grip = (1 - w.f1) * 0.34;             // moulded-in grip texture
    const flow = Math.sin((u * 2.4 + tileableFbm(u, v, 3, 3) * 2.0) * TAU) * 0.05;
    return peel + grip + flow;
  }, 1.05);
  T.polyORM = ormFromFields(512, (u, v) => {
    const n = tileableFbm(u, v, 14, 4, 0.5);
    const peel = tileableFbm(u, v, 34, 3, 0.55);
    /* Polymer does not scratch to bare metal, it BURNISHES: the moulded texture
     * gets flattened where the part is rubbed, so a scuff on a handguard is a
     * smooth patch, not a bright line. Roughness only — metalness stays 0. */
    const scuff = scratches(u, v, 9, 6, 0.68, 0.29) * 0.6 + filmField(u, v, 5, 0.37) * 0.4;
    return [clamp(0.50 + n * 0.42 + peel * 0.42 - scuff * 0.30, 0, 1), 0.0,
      clamp(0.84 + n * 0.16 - scuff * 0.06, 0, 1)];
  });

  /* ---- per-family albedo -------------------------------------------- *
   * The base colour is baked per family rather than carried on
   * `material.color`, because the detail field is multiplicative and each
   * finish modulates its own colour differently: phosphate skews warm where
   * it is thin, anodising pin-holes, polymer shows glass fibre lighter than
   * its matrix. This is the ONLY place a weapon hue is authored. */
  const NRM = {
    anod: T.anodNormal, blast: T.blastNormal, phos: T.phosNormal,
    bare: T.bareNormal, poly: T.polyNormal, fde: T.fdeNormal,
  };
  const ORM = {
    anod: T.anodORM, blast: T.blastORM, phos: T.phosORM,
    bare: T.bareORM, poly: T.polyORM, fde: T.polyORM,
  };
  const ALB = {
    anod: (base) => albedoFromFields(512, (u, v) => {
      const streak = 1 + Math.sin(u * TAU * 26 + tileableFbm(u, v, 4, 3) * 4) * 0.045
        + tileableFbm(u * 3.0, v * 0.12, 9, 3, 0.5) * 0.13;
      const cloud = 1 + tileableFbm(u, v, 4.5, 4, 0.5) * 0.16;
      const w = worley2(u * 24, v * 24, 24);
      const pit = 1 - smoothstep(0.07, 0.0, w.f1) * 0.20;
      // a scratch shows the aluminium: several times the coating's own value
      const sc = scratches(u, v, 11, 9, 0.70, 0.31) + scratches(v, u, 17, 7, 0.74, 0.63) * 0.7;
      const film = filmField(u, v, 6, 0.19);
      const k = streak * cloud * pit * (1 + film * 0.16);
      return [
        base[0] * k + sc * 0.150,
        base[1] * k + sc * 0.152,
        base[2] * k + sc * 0.156,
      ];
    }),
    blast: (base) => albedoFromFields(512, (u, v) => {
      const w = worley2(u * 96, v * 96, 96);
      const grain = 0.92 + w.f1 * 0.20;
      const cloud = 1 + tileableFbm(u, v, 5.0, 4, 0.5) * 0.11;
      const sc = scratches(u, v, 14, 8, 0.74, 0.47);
      const film = filmField(u, v, 7, 0.53);
      const k = grain * cloud * (1 + film * 0.14);
      return [base[0] * k + sc * 0.115, base[1] * k + sc * 0.117, base[2] * k + sc * 0.121];
    }),
    phos: (base) => albedoFromFields(512, (u, v) => {
      const w = worley2(u * 58, v * 58, 58);
      const w2 = worley2(u * 152, v * 152, 152);
      const crystal = 0.90 + w.f1 * 0.32 + (1 - w2.f1) * 0.06;
      const blotch = 1 + tileableFbm(u, v, 3.5, 4, 0.55) * 0.17
        + tileableFbm(u + 0.31, v + 0.77, 11, 3, 0.5) * 0.08;
      const sc = scratches(u, v, 13, 8, 0.72, 0.11);
      const film = filmField(u, v, 5, 0.83);
      const k = crystal * blotch * (1 + film * 0.15);
      const warm = 1 + tileableFbm(u + 0.5, v, 5, 3) * 0.10;
      return [
        base[0] * k * warm + sc * 0.105,
        base[1] * k + sc * 0.107,
        base[2] * k * (2 - warm) + sc * 0.111,
      ];
    }),
    /*
     * Steel, not nickel.
     *
     * A bare-metal part's *colour* is its F0, and an F0 that is one constant
     * bright neutral over a low-roughness surface is a mirror — which is
     * exactly the "flat bright sky reflection" the previous pass flagged and
     * correctly refused to fix by dimming. What separates photographed steel
     * from chrome is that it is never one tone: there is a slow macro
     * variation from forging and heat, a directional draw polish, dark
     * handling stains where hands and cases have rubbed it, and fine pitting.
     * All four are here, and none of them lowers the peak specular.
     */
    bare: (base) => albedoFromFields(512, (u, v) => {
      const draw = 1 + tileableFbm(u * 2.2, v * 0.06, 26, 3, 0.5) * 0.16;
      const macro = 1 + tileableFbm(u + 0.41, v + 0.13, 2.4, 4, 0.58) * 0.20;
      // The handling stains used to sit at worley-18 with 0.34 of contrast.
      // At 64 tiles/metre that is a 0.9 mm cell, i.e. ~8 screen pixels at
      // viewmodel range — it read as porous white stone, not as steel. The
      // stains now live on the MACRO tap (see applySurface) where they belong,
      // and what is left here is only a faint tooth.
      const w = worley2(u * 18, v * 18, 18);
      const stain = 1 - smoothstep(0.30, 0.02, w.f1) * 0.11;
      const w2 = worley2(u * 76, v * 76, 76);
      const pit = 1 - smoothstep(0.06, 0.0, w2.f1) * 0.12;
      const k = draw * macro * stain * pit;
      // steel carries a faint warm-grey patina; a perfectly neutral F0 is what
      // makes a low-roughness surface read as plated rather than as metal.
      const warm = 1 + tileableFbm(u + 0.83, v + 0.29, 4.0, 3, 0.55) * 0.11;
      return [base[0] * k * warm, base[1] * k, base[2] * k * (2 - warm)];
    }),
    poly: (base) => albedoFromFields(512, (u, v) => {
      const peel = tileableFbm(u, v, 34, 4, 0.55);
      const flow = Math.sin((u * 3.1 + tileableFbm(u, v, 4, 3) * 2.4) * TAU);
      const fibre = smoothstep(0.62, 0.90, tileableFbm(u, v, 90, 2, 0.5) * 0.5 + 0.5) * 0.26;
      return tint(base, 1 + peel * 0.16 + flow * 0.05 + fibre);
    }),
    fde: (base) => albedoFromFields(512, (u, v) => {
      const peel = tileableFbm(u, v, 22, 4, 0.55);
      const w = worley2(u * 66, v * 66, 66);
      // Cerakote/FDE furniture mottles far more than a black moulding does,
      // and the pigment sits lighter on the raised grip texture.
      const mottle = tileableFbm(u + 0.19, v + 0.53, 6, 4, 0.55);
      return tint(base, 1 + peel * 0.14 + mottle * 0.13 + (1 - w.f1) * 0.10);
    }),
  };

  T.family = {};
  for (const [key, f] of Object.entries(FAMILY)) {
    T.family[key] = {
      albedo: ALB[f.cls](rgb(f.color)),
      normal: NRM[f.cls],
      orm: ORM[f.cls],
    };
  }

  /* ---------------------------------------------------------------- *
   * 4. Nomex / goatskin glove.
   * ---------------------------------------------------------------- */
  T.clothNormal = normalFromHeight(512, (u, v) => {
    const wu = Math.sin(u * TAU * 88) * 0.5 + 0.5;
    const wv = Math.sin(v * TAU * 88) * 0.5 + 0.5;
    const weave = Math.max(wu, wv) * 0.62 + Math.min(wu, wv) * 0.14;
    const grain = worley2(u * 130, v * 130, 130).f1;   // suede pebbling
    return weave * 0.7 + grain * 0.32 + tileableFbm(u, v, 60, 3) * 0.16;
  }, 0.95);

  {
    /*
     * The glove was authored at 0.078 linear so it would sit "~45% darker than
     * the receiver" — but the receiver was a bright olive then. Against the
     * iteration-4 palette that made both gloves solid black holes with no
     * readable form, and it is what turned the right thumb into a black sliver
     * across the receiver. It is lifted just far enough to hold form; the
     * separation from the weapon now comes from roughness (0.83-0.96 against
     * the upper's 0.275-0.415) and from the sheen lobe, which is what actually
     * distinguishes goatskin from anodised aluminium.
     */
    const base = [0.112, 0.106, 0.098];
    T.clothAlbedo = albedoFromFields(512, (u, v) => {
      const wu = Math.sin(u * TAU * 88) * 0.5 + 0.5;
      const wv = Math.sin(v * TAU * 88) * 0.5 + 0.5;
      const weave = 0.86 + Math.max(wu, wv) * 0.30;
      const dirt = 1 + tileableFbm(u, v, 6, 4, 0.55) * 0.22;
      const pebble = 0.94 + worley2(u * 130, v * 130, 130).f1 * 0.20;
      return tint(base, weave * dirt * pebble);
    });

    T.clothORM = ormFromFields(512, (u, v) => {
      const n = tileableFbm(u, v, 20, 4, 0.5);
      const wu = Math.sin(u * TAU * 88) * 0.5 + 0.5;
      return [0.885 + n * 0.06 - wu * 0.04, 0.0, 0.72 + n * 0.28];
    });
  }

  /* ---------------------------------------------------------------- *
   * 5. Multicam-ish sleeve.
   * ---------------------------------------------------------------- */
  T.camo = dataTex(256, (data, size) => {
    // Every entry used to be a warm brown, so the whole sleeve lit as one
    // chocolate tube. Multicam works because the tans are cut by greens and
    // greys; the pattern has to carry its own value structure or the garment
    // reads as a solid cylinder no matter how good the weave normal is.
    const cols = [
      [0.062, 0.060, 0.046], [0.092, 0.084, 0.058], [0.041, 0.045, 0.037],
      [0.074, 0.068, 0.048], [0.028, 0.031, 0.027],
    ];
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const u = x / size, v = y / size;
        const a = tileableFbm(u, v, 5, 4, 0.55);
        const b = tileableFbm(u + 0.37, v + 0.11, 9, 3, 0.5);
        let idx = Math.floor(((a * 0.6 + b * 0.4) * 0.5 + 0.5) * cols.length * 1.4) % cols.length;
        if (idx < 0) idx += cols.length;
        const c = cols[idx];
        const grain = 1 + tileableFbm(u, v, 70, 2) * 0.12;
        const i = (y * size + x) * 4;
        data[i] = clamp(c[0] * grain, 0, 1) * 255;
        data[i + 1] = clamp(c[1] * grain, 0, 1) * 255;
        data[i + 2] = clamp(c[2] * grain, 0, 1) * 255;
        data[i + 3] = 255;
      }
    }
  }, { srgb: true });

  /* ---------------------------------------------------------------- *
   * 6. Rubber (buttpad, overmould, optic liner) and brass.
   * ---------------------------------------------------------------- */
  T.rubberAlbedo = albedoFromFields(256, (u, v) => {
    const w = worley2(u * 46, v * 46, 46);
    const k = 0.84 + w.f1 * 0.32 + tileableFbm(u, v, 12, 3) * 0.10;
    return tint([0.020, 0.021, 0.022], k);
  });
  T.rubberORM = ormFromFields(256, (u, v) => {
    const w = worley2(u * 46, v * 46, 46);
    return [0.905 + (1 - w.f1) * 0.06, 0.0, 0.74 + w.f1 * 0.26];
  });

  T.brassAlbedo = albedoFromFields(256, (u, v) => {
    const k = 1 + tileableFbm(u, v, 18, 3, 0.5) * 0.10;
    return tint([0.560, 0.430, 0.170], k);
  });
  T.brassORM = ormFromFields(256, (u, v) => {
    const n = tileableFbm(u, v, 22, 3, 0.5);
    return [0.245 + n * 0.09, 1.0, 0.88 + n * 0.12];
  });

  /* ---------------------------------------------------------------- *
   * 7. Walnut, for the shotgun furniture.
   * ---------------------------------------------------------------- */
  T.woodAlbedo = albedoFromFields(512, (u, v) => {
    const wob = tileableFbm(u * 0.5, v * 4, 6, 4, 0.55) * 0.35;
    const rings = Math.sin((v * 26 + wob * 9) * Math.PI) * 0.5 + 0.5;
    const k = 0.74 + Math.pow(rings, 2.2) * 0.55 + tileableFbm(u, v, 40, 3) * 0.10;
    return [0.150 * k, 0.076 * k * 1.02, 0.038 * k * 1.1];
  });
  T.woodORM = ormFromFields(512, (u, v) => {
    const wob = tileableFbm(u * 0.5, v * 4, 6, 4, 0.55) * 0.35;
    const rings = Math.sin((v * 26 + wob * 9) * Math.PI) * 0.5 + 0.5;
    return [0.325 + (1 - rings) * 0.10, 0.0, 0.86 + rings * 0.14];
  });

  /* ---------------------------------------------------------------- *
   * 8. Webbing / cordura.
   * ---------------------------------------------------------------- */
  T.webAlbedo = albedoFromFields(256, (u, v) => {
    const wu = Math.sin(u * TAU * 40) * 0.5 + 0.5;
    const wv = Math.sin(v * TAU * 20) * 0.5 + 0.5;
    const k = 0.80 + Math.max(wu, wv) * 0.38 + tileableFbm(u, v, 10, 3) * 0.12;
    // 0.030 linear is darker than the glove it is sewn onto, so every seam,
    // welt and cuff strap printed as a black slash rather than as webbing —
    // most visibly the wrist strap crossing the receiver in `wpn_dbg_grip`.
    return tint([0.052, 0.050, 0.045], k);
  });
  T.webORM = ormFromFields(256, (u, v) => {
    const n = tileableFbm(u, v, 16, 3, 0.5);
    return [0.925 + n * 0.05, 0.0, 0.70 + n * 0.30];
  });

  return T;
}

/* ------------------------------------------------------------------ *
 * Roll marks / selector markings atlas
 * ------------------------------------------------------------------ */

export const MARK = {};

function buildMarkAtlas() {
  const S = 1024;
  const cv = document.createElement('canvas');
  cv.width = cv.height = S;
  const g = cv.getContext('2d');
  g.clearRect(0, 0, S, S);
  g.fillStyle = '#ffffff';
  g.textBaseline = 'middle';

  /** Register a cell in UV space (v measured from the bottom, like three.js UVs). */
  const cell = (name, x, y, w, h, draw) => {
    g.save();
    g.translate(x, y);
    g.beginPath();
    g.rect(0, 0, w, h);
    g.clip();
    draw(g, w, h);
    g.restore();
    MARK[name] = { u0: x / S, v0: 1 - (y + h) / S, u1: (x + w) / S, v1: 1 - y / S, aspect: w / h };
  };

  const text = (ctx, str, size, x, y, opts = {}) => {
    ctx.font = `${opts.weight || 700} ${size}px ${opts.font || '"Helvetica Neue", Helvetica, Arial, sans-serif'}`;
    ctx.textAlign = opts.align || 'left';
    ctx.letterSpacing = opts.spacing || '0px';
    ctx.fillText(str, x, y);
  };

  /**
   * Selector index marks. A real fire-control selector reads SAFE / SEMI / AUTO,
   * arranged around the pivot at 90 / 0 / -45 degrees, with a pointer arrow.
   * (The previous build only carried these on the LEFT flank; the closeup camera
   * looks at the RIGHT flank, where it found the proof-stamp cell instead and read
   * it as a selector saying "MP / HP / P". Both flanks are marked now, which is
   * also what an ambidextrous selector actually looks like.)
   */
  const drawSelector = (c, w, h) => {
    c.save();
    c.strokeStyle = '#ffffff';
    c.lineWidth = 4;
    const cx = 44, cy = 64, r = 48;
    const labels = [['SAFE', Math.PI * 0.5], ['SEMI', 0], ['AUTO', -Math.PI * 0.44]];
    for (const [label, a] of labels) {
      c.beginPath();
      c.moveTo(cx + Math.cos(a) * (r - 14), cy - Math.sin(a) * (r - 14));
      c.lineTo(cx + Math.cos(a) * r, cy - Math.sin(a) * r);
      c.stroke();
      text(c, label, 22, cx + Math.cos(a) * (r + 9), cy - Math.sin(a) * (r + 9), { spacing: '1px' });
    }
    c.restore();
  };

  // ---- receiver roll mark (left side of the magwell) -----------------------
  cell('roll_receiver', 0, 0, 512, 128, (c) => {
    text(c, 'COLT DEFENSE LLC', 30, 6, 24, { spacing: '1.5px' });
    text(c, 'WEST HARTFORD, CONN. U.S.A.', 21, 6, 56, { spacing: '1px', weight: 500 });
    text(c, 'M4A1 CARBINE  CAL. 5.56 MM  NATO', 23, 6, 88, { spacing: '0.5px' });
    text(c, 'SER.  W 0 4 4 7 3 1 9', 20, 6, 116, { spacing: '1px', weight: 500 });
  });

  cell('selector', 512, 0, 256, 128, drawSelector);
  cell('selector_r', 768, 128, 256, 128, drawSelector);

  // ---- magazine markings ---------------------------------------------------
  cell('mag_mark', 768, 0, 256, 128, (c) => {
    text(c, '5.56x45', 30, 6, 26, { spacing: '1px' });
    text(c, 'NATO', 30, 6, 60, { spacing: '3px' });
    text(c, 'USGI  30 RND', 18, 6, 92, { spacing: '1px', weight: 500 });
    c.strokeStyle = '#ffffff'; c.lineWidth = 3;
    c.strokeRect(150, 10, 92, 42);
    text(c, 'MAG', 22, 196, 32, { align: 'center' });
  });

  // ---- optic markings ------------------------------------------------------
  cell('optic_mark', 0, 128, 256, 96, (c) => {
    text(c, 'MICRO T-2', 26, 6, 22, { spacing: '2px' });
    text(c, '2 MOA  RED DOT', 17, 6, 50, { spacing: '1px', weight: 500 });
    c.strokeStyle = '#ffffff'; c.lineWidth = 3;
    c.beginPath(); c.moveTo(8, 76); c.lineTo(30, 62); c.lineTo(30, 90); c.closePath(); c.fill();
    text(c, 'UP', 18, 40, 76, { weight: 600 });
  });

  // ---- proof / inspection stamps ------------------------------------------
  // Real proof marks: a crossed-circle inspector's stamp, a NATO cross-in-circle
  // and the calibre. No free-floating "MP / HP / P".
  cell('proof', 256, 128, 160, 96, (c) => {
    c.strokeStyle = '#ffffff'; c.lineWidth = 4;
    c.beginPath(); c.arc(30, 30, 22, 0, TAU); c.stroke();
    c.beginPath(); c.moveTo(30, 10); c.lineTo(30, 50); c.moveTo(10, 30); c.lineTo(50, 30); c.stroke();
    c.lineWidth = 3;
    c.beginPath(); c.arc(88, 30, 20, 0, TAU); c.stroke();
    c.beginPath(); c.moveTo(88, 14); c.lineTo(88, 46); c.moveTo(72, 30); c.lineTo(104, 30); c.stroke();
    text(c, '5.56 NATO', 19, 6, 72, { spacing: '1px', weight: 600 });
  });

  // ---- shotgun / pistol / smg roll marks ----------------------------------
  cell('roll_shotgun', 416, 128, 352, 96, (c) => {
    text(c, 'REMINGTON  MODEL 870', 26, 6, 24, { spacing: '1.2px' });
    text(c, '12 GA.  2 3/4"  MAGNUM', 20, 6, 56, { spacing: '1px', weight: 500 });
    text(c, 'ILION, N.Y.  MADE IN U.S.A.', 16, 6, 84, { spacing: '1px', weight: 500 });
  });

  cell('roll_pistol', 0, 224, 448, 96, (c) => {
    text(c, 'GLOCK  17  AUSTRIA', 28, 6, 24, { spacing: '2px' });
    text(c, '9x19', 26, 6, 58, { spacing: '2px' });
    text(c, 'GES.M.B.H.  9mm LUGER', 17, 6, 86, { spacing: '1px', weight: 500 });
  });

  cell('roll_smg', 448, 224, 448, 96, (c) => {
    text(c, 'MP5A3  KAL. 9mm x 19', 28, 6, 24, { spacing: '1.5px' });
    text(c, 'HECKLER & KOCH  OBERNDORF/N', 20, 6, 56, { spacing: '1px', weight: 500 });
    text(c, 'MADE IN GERMANY', 17, 6, 84, { spacing: '2px', weight: 500 });
  });

  // ---- fire-mode pictograms (HK-style) ------------------------------------
  cell('hk_selector', 0, 320, 192, 128, (c) => {
    c.fillStyle = '#ffffff';
    const bullet = (x, y, s) => {
      c.save(); c.translate(x, y); c.scale(s, s);
      c.beginPath();
      c.moveTo(-4, 10); c.lineTo(4, 10); c.lineTo(4, -4);
      c.quadraticCurveTo(0, -14, -4, -4); c.closePath(); c.fill();
      c.restore();
    };
    c.save(); c.translate(30, 30); c.rotate(0.5); bullet(0, 0, 1.4); c.restore();
    bullet(96, 30, 1.4); bullet(116, 30, 1.4); bullet(136, 30, 1.4);
    c.lineWidth = 7; c.strokeStyle = '#ffffff';
    c.beginPath(); c.moveTo(18, 84); c.lineTo(48, 114); c.moveTo(48, 84); c.lineTo(18, 114); c.stroke();
    text(c, 'S  E  F', 26, 96, 100, { spacing: '4px' });
  });

  // ---- serials for the smaller guns ---------------------------------------
  cell('serial_small', 192, 320, 256, 64, (c) => {
    text(c, 'AXH 4 2 8 1 7', 26, 6, 24, { spacing: '3px', weight: 500 });
    text(c, 'MADE IN U.S.A.', 16, 6, 52, { spacing: '2px', weight: 500 });
  });

  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.needsUpdate = true;

  // A matching normal map so the engraving actually catches a highlight.
  const img = g.getImageData(0, 0, S, S).data;
  const NS = 512;
  // DataTexture never flips on upload while CanvasTexture does, so sample the
  // canvas bottom-up to keep the normal map registered with the albedo.
  const height = (x, y) => {
    const sx = Math.min(S - 1, Math.max(0, Math.floor(x * S / NS)));
    const sy = Math.min(S - 1, Math.max(0, Math.floor((NS - 1 - y) * S / NS)));
    return -img[(sy * S + sx) * 4 + 3] / 255; // engraved: alpha = depth
  };
  const nrm = dataTex(NS, (data) => {
    const at = (x, y) => height(clamp(x, 0, NS - 1), clamp(y, 0, NS - 1));
    for (let y = 0; y < NS; y++) {
      for (let x = 0; x < NS; x++) {
        const dx = at(x + 1, y) - at(x - 1, y);
        const dy = at(x, y + 1) - at(x, y - 1);
        const nx = -dx * 3.2, ny = -dy * 3.2, nz = 1;
        const l = Math.hypot(nx, ny, nz);
        const i = (y * NS + x) * 4;
        data[i] = ((nx / l) * 0.5 + 0.5) * 255;
        data[i + 1] = ((ny / l) * 0.5 + 0.5) * 255;
        data[i + 2] = ((nz / l) * 0.5 + 0.5) * 255;
        data[i + 3] = 255;
      }
    }
  });
  nrm.wrapS = nrm.wrapT = THREE.ClampToEdgeWrapping;

  return { tex, nrm };
}

/* ------------------------------------------------------------------ *
 * Reticle
 * ------------------------------------------------------------------ */

function buildReticle() {
  const S = 128;
  const data = new Uint8Array(S * S * 4);
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const dx = (x + 0.5) / S - 0.5, dy = (y + 0.5) / S - 0.5;
      const d = Math.hypot(dx, dy) * 2; // 0 at centre, 1 at edge
      const core = Math.exp(-Math.pow(d / 0.15, 2) * 3.0);
      const halo = Math.exp(-Math.pow(d / 0.40, 2) * 2.4) * 0.16;
      const a = clamp(core + halo, 0, 1);
      const i = (y * S + x) * 4;
      data[i] = 255;
      data[i + 1] = clamp(a * 0.26, 0, 1) * 255;
      data[i + 2] = clamp(a * 0.10, 0, 1) * 255;
      data[i + 3] = a * 255;
    }
  }
  const t = new THREE.DataTexture(data, S, S, THREE.RGBAFormat);
  t.colorSpace = THREE.SRGBColorSpace;
  t.magFilter = THREE.LinearFilter;
  t.minFilter = THREE.LinearFilter;
  t.needsUpdate = true;
  return t;
}

/* ------------------------------------------------------------------ *
 * Shader patch: packed ORM + edge wear + carbon fouling + baked cavity AO
 * ------------------------------------------------------------------ */

/**
 * One sampler carries roughness (R), metalness (G) and cavity AO (B); three.js
 * would spend one texture unit each on `roughnessMap`, `metalnessMap` and `aoMap`
 * even when they are the same object, and FINDINGS F6 says we have about four to
 * spend in total. `map`, `normalMap`, `uORM` and the environment is exactly four.
 *
 * `roughRange` maps the stored 0..1 into the material's authored roughness band,
 * which is what gives the receiver, the rail and the handguard three *visibly*
 * different specular sweep widths rather than one shared rolloff.
 */
/*
 * Wear was the single largest error in the previous shader. `uWearColor` is a
 * bare-metal F0 (linear ~0.24) against a coating albedo of ~0.015, and it was
 * mixed into the albedo LINEARLY — so even the 0.09 mean wear left on a mesh
 * multiplied the effective diffuse albedo by ~2.5x and washed the whole weapon to
 * a flat mid grey. Ablation on wpn_closeup: forcing the albedo to black dropped
 * the receiver from 0.293 to 0.140 sRGB, and every bit of that surviving 0.140 was
 * this term. Squaring the weight keeps the bright metal tint where a chamfer is
 * really polished through and leaves the authored albedo alone everywhere else;
 * metalness still ramps linearly, so the SPECULAR colour reaches bare steel at
 * full wear, which is where a bright value belongs.
 */
/* ====================================================================== *
 * ITERATION 5 — the two things the blind test against shipped Call of Duty
 * actually found (FINDINGS F20)
 *
 * `wpn_closeup` scored 2.7 against a shipped floor of 7.0. Measured on the
 * frame, against the same statistics taken over the rifle in `refs/mw3_04.jpg`:
 *
 *              HSV saturation p50   value p25..p75      local detail std p50
 *   shipped          0.138            0.231..0.553            0.087
 *   ours             0.295            0.263..0.400            0.026
 *
 * Three separate defects, and none of them is hue spread (FINDINGS F15 already
 * settled that hue is the wrong axis, and the rendered hue band IS inside the
 * corrected 6-12 deg window). They are:
 *
 *  1. TWICE THE CHROMA. A rifle photographed in warm sun sits at saturation
 *     0.14; ours sits at 0.30 and reads as milk chocolate. The coatings were
 *     already authored cool to pre-correct for the key — it was not enough,
 *     because the correction has to beat a 45:1 key AND an IBL floor.
 *  2. HALF THE VALUE RANGE. The shipped weapon runs 0.23 to 0.55 across its
 *     own body; ours runs 0.26 to 0.40. It has no darks and no brights of its
 *     own — every recess sits at the same value as the flat beside it and no
 *     arris carries a highlight.
 *  3. A THIRD OF THE DETAIL ENERGY. 0.026 against 0.087 of local luminance
 *     standard deviation. Everything is a smooth gradient at 10 cm.
 *
 * All three are addressed by things that cost no texture unit (F6 still caps
 * us at four: map, normalMap, uORM, envMap):
 *
 *   CURVATURE EDGE WEAR   `aWear` finds chamfers by vertex normal, which only
 *                         works on the flat-shaded extrusions — a lathe or a
 *                         loft reports half its surface as a chamfer, so the
 *                         builder cuts those parts to 0.22 and their fillets
 *                         got nothing. A screen-space curvature in 1/metres
 *                         covers exactly the complement: it is meaningless
 *                         inside a flat facet and precise on a smooth one.
 *                         The two are combined with max().
 *   CAVITY GRIME          `aAO` and the ORM cavity were only scaling LIGHT.
 *                         Dust and carbon are pigment: they darken the albedo
 *                         and raise the roughness in the recess itself, which
 *                         is where the missing bottom half of the value range
 *                         is.
 *   CONTACT BURNISH       `aTouch` (new) — the roughness FIELD. Not noise: the
 *                         places a hand, a sling and a magazine actually touch.
 *   OBJECT-SPACE MACRO    the old macro tap re-read the ORM through triplanar
 *                         UVs, so it stepped discontinuously wherever the
 *                         dominant axis changed. In object space it is
 *                         continuous over the whole rifle.
 * ====================================================================== */
function applySurface(mat, opts = {}) {
  const orm = opts.orm ?? null;
  const wearColor = new THREE.Color(opts.wearColor ?? 0x6e6a63);
  const wearRough = opts.wearRough ?? 0.26;
  const grimeColor = new THREE.Color(opts.grimeColor ?? 0x0a0908);
  const rMin = opts.roughMin ?? 0.0;
  const rMax = opts.roughMax ?? 1.0;
  const mScale = opts.metalScale ?? 1.0;
  const aoStrength = opts.ao ?? 0.85;

  mat.userData.wearColor = wearColor;
  mat.userData.roughBand = [rMin, rMax];

  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uORM = { value: orm };
    shader.uniforms.uWearColor = { value: wearColor };
    shader.uniforms.uWearRough = { value: wearRough };
    shader.uniforms.uGrimeColor = { value: grimeColor };
    shader.uniforms.uRough = { value: new THREE.Vector2(rMin, rMax - rMin) };
    shader.uniforms.uMetalScale = { value: mScale };
    shader.uniforms.uAOStrength = { value: aoStrength };
    shader.uniforms.uSkyShadow = { value: opts.skyShadow ?? 0.28 };
    shader.uniforms.uSkySpec = { value: opts.skySpec ?? 0.30 };
    /*
     * MACRO TAP — the second-largest error in this shader, and free to fix.
     *
     * `WeaponBuilder` lays triplanar UVs at 64 tiles/metre, so EVERY texture in
     * this file repeats once every 15.6 mm. Nothing authored into a 512 map can
     * therefore be macro: the "wide roughness field" and "handling stains" the
     * bare-metal fix asked for were tiling four times across a charging handle
     * and averaging back to one gloss level, which is exactly the flat mirror
     * that reads as chrome.
     *
     * The fix costs no texture unit and no memory: sample the SAME packed ORM a
     * second time at 1/11 the UV rate — one repeat every ~17 cm, i.e. genuine
     * macro over a 75 cm rifle — and use it to push tone and roughness around.
     * FINDINGS F6 still holds: map, normalMap, uORM, envMap. Four.
     */
    shader.uniforms.uMacroScale = { value: opts.macroScale ?? 0.0909 };
    shader.uniforms.uMacroTone = { value: opts.macroTone ?? 0.10 };
    shader.uniforms.uMacroRough = { value: opts.macroRough ?? 0.09 };
    /* Curvature edge wear.
     *   x  strength
     *   y  curvature onset, 1/metres. Authored below as a RADIUS: a machinist's
     *      chamfer on a receiver is 0.4-1.0 mm, a lathe fillet 1-3 mm, and the
     *      handguard tube itself is 15 mm — so an onset near 1/2.5 mm picks the
     *      first two and leaves the third alone.
     *   z  full-wear curvature, 1/metres
     *   w  how much of the edge is missed on purpose (patchiness floor)
     */
    shader.uniforms.uEdgeP = {
      value: new THREE.Vector4(
        opts.edgeWear ?? 0.85,
        1 / Math.max(opts.edgeRadius ?? 0.0025, 1e-5),
        1 / Math.max(opts.edgeRadiusFull ?? 0.0006, 1e-5),
        opts.edgePatch ?? 0.45,
      ),
    };
    /* Cavity grime: x albedo darkening, y roughness rise, z touch burnish
     * (roughness DROP where a hand lands), w touch albedo darkening. */
    shader.uniforms.uCavP = {
      value: new THREE.Vector4(
        opts.cavityDirt ?? 0.42,
        opts.cavityRough ?? 0.16,
        opts.touchPolish ?? 0.20,
        opts.touchDirt ?? 0.10,
      ),
    };
    shader.uniforms.uCavColor = { value: new THREE.Color(opts.cavityColor ?? 0x0d0c0a) };
    // Object-space macro field: metres per cycle for the two octaves.
    shader.uniforms.uObjMacro = {
      value: new THREE.Vector2(1 / Math.max(opts.objMacroMetres ?? 0.075, 1e-4),
        opts.objMacroTone ?? 0.14),
    };

    shader.vertexShader = `
attribute float aWear;
attribute float aGrime;
attribute float aAO;
attribute float aSky;
attribute float aTouch;
varying float vWear;
varying float vGrime;
varying float vAO;
varying float vSky;
varying float vTouch;
varying vec3 vGunP;
${shader.vertexShader}`.replace(
      '#include <begin_vertex>',
      '#include <begin_vertex>\n\tvWear = aWear;\n\tvGrime = aGrime;\n\tvAO = aAO;\n\tvSky = aSky;'
      + '\n\tvTouch = aTouch;\n\tvGunP = position;');

    shader.fragmentShader = `
uniform sampler2D uORM;
uniform vec3 uWearColor;
uniform float uWearRough;
uniform vec3 uGrimeColor;
uniform vec2 uRough;
uniform float uMetalScale;
uniform float uAOStrength;
uniform float uSkyShadow;
uniform float uSkySpec;
uniform float uMacroScale;
uniform float uMacroTone;
uniform float uMacroRough;
uniform vec4 uEdgeP;
uniform vec4 uCavP;
uniform vec3 uCavColor;
uniform vec2 uObjMacro;
varying float vWear;
varying float vGrime;
varying float vAO;
varying float vSky;
varying float vTouch;
varying vec3 vGunP;
vec3 gunORM;
float gunMacro;
float gunObj;     // object-space macro field, -0.5 .. 0.5
float gunEdge;    // curvature edge wear, 0..1, already combined with vWear
float gunCav;     // 0 open, 1 deep in a recess

float gunHash(vec3 p){
	p = fract( p * 0.3183099 + vec3( 0.71, 0.113, 0.419 ) );
	p *= 17.0;
	return fract( p.x * p.y * p.z * ( p.x + p.y + p.z ) );
}
float gunNoise(vec3 p){
	vec3 i = floor( p ), f = fract( p );
	f = f * f * ( 3.0 - 2.0 * f );
	return mix(
		mix( mix( gunHash( i + vec3(0,0,0) ), gunHash( i + vec3(1,0,0) ), f.x ),
		     mix( gunHash( i + vec3(0,1,0) ), gunHash( i + vec3(1,1,0) ), f.x ), f.y ),
		mix( mix( gunHash( i + vec3(0,0,1) ), gunHash( i + vec3(1,0,1) ), f.x ),
		     mix( gunHash( i + vec3(0,1,1) ), gunHash( i + vec3(1,1,1) ), f.x ), f.y ), f.z );
}
${shader.fragmentShader}`
      // sample once, as early as the UV varying is available, plus the macro tap
      .replace('#include <map_fragment>', `#include <map_fragment>
	gunORM = texture2D( uORM, vMapUv ).rgb;
	gunMacro = texture2D( uORM, vMapUv * uMacroScale ).r - 0.5;
	{
		/* OBJECT-SPACE MACRO. The tap above re-reads the packed ORM through the
		 * builder's triplanar UVs, so it is a different function of position on
		 * every face and STEPS wherever the dominant axis changes — which on a
		 * rifle is every second edge. A 3D field in bind-pose weapon space is
		 * continuous over the whole part, and it is the only modulation here that
		 * survives the mip chain at any distance because it is not a texture. */
		vec3 op = vGunP * uObjMacro.x;
		gunObj = gunNoise( op ) * 0.62 + gunNoise( op * 3.17 + 11.3 ) * 0.38 - 0.5;

		/* CURVATURE EDGE WEAR, in inverse metres.
		 *
		 * dFdx(-vViewPosition) is metres per pixel and dFdx(vNormal) is the
		 * normal's change per pixel, so the ratio below is a real curvature with a
		 * real unit — which is what makes the threshold authorable as a radius and
		 * makes the effect identical at 10 cm and at 3 m instead of being a
		 * screen-space width.
		 *
		 * vNormal is the GEOMETRIC normal, deliberately: the normal map's micro
		 * relief would report every texel as an edge. On the flat-shaded extrusions
		 * that make up the receiver this is ~0 inside a facet and aWear carries
		 * the chamfer; on the lathed and lofted parts — barrel, handguard tube,
		 * optic body, buffer tube, grip — it is exact, and those are precisely the
		 * parts WeaponBuilder has to cut to 0.22 because its normal-direction test
		 * mistakes half a cylinder for a chamfer. Complementary, hence max(). */
		vec3 vp = -vViewPosition;
		vec3 gn = normalize( vNormal );
		vec3 dpx = dFdx( vp ), dpy = dFdy( vp );
		vec3 dnx = dFdx( gn ), dny = dFdy( gn );
		float den = dot( dpx, dpx ) + dot( dpy, dpy ) + 1e-12;
		float k = ( dot( dnx, dpx ) + dot( dny, dpy ) ) / den;
		float eGeo = smoothstep( uEdgeP.y, uEdgeP.z, max( k, 0.0 ) );
		// A perfectly even bright line down every edge is its own tell. Real wear
		// is heavier where the part is handled and absent in places.
		// NB: 'patch' is a RESERVED WORD in GLSL ES 3.00 (tessellation), and ANGLE
		// rejects it with "Illegal use of reserved word". Named edgePatchK.
		float edgePatchK = mix( uEdgeP.w, 1.0,
			smoothstep( 0.30, 0.72, gunNoise( vGunP * 26.0 ) * 0.65
				+ gunNoise( vGunP * 91.0 + 5.1 ) * 0.35 ) );
		gunEdge = clamp( max( vWear, eGeo * uEdgeP.x * edgePatchK ), 0.0, 1.0 );

		// cavity: the baked hemisphere occlusion and the map's own cavity channel
		gunCav = clamp( 1.0 - gunORM.b * vAO, 0.0, 1.0 );
	}`)
      // albedo: polished bare metal on the chamfers, carbon soot in the gas path
      .replace('#include <color_fragment>', `#include <color_fragment>
	// Macro tone: one slow modulation over ~17 cm, so a burnished part is not one
	// value from end to end. Everything else in this file tiles every 15.6 mm.
	diffuseColor.rgb *= 1.0 + gunMacro * uMacroTone * 2.0;
	diffuseColor.rgb *= 1.0 + gunObj * uObjMacro.y * 2.0;
	/* DUST AND CARBON ARE PIGMENT, not a light multiplier.
	 * The cavity channels were only ever scaling reflectedLight, so a rail slot,
	 * a magwell throat and the shelf under the optic sat at the same ALBEDO as the
	 * flat beside them and only went dark when the light happened to miss them.
	 * A recess on a carried weapon is full of dust and combustion residue; that is
	 * where the bottom half of the reference frame's value range lives. */
	diffuseColor.rgb = mix( diffuseColor.rgb, uCavColor, gunCav * gunCav * uCavP.x );
	// Wear tints the albedo QUADRATICALLY while metalness rises linearly.
	// See the applySurface() header for the ablation that motivated this.
	/* A THRESHOLD, NOT A SQUARE.
	 * The quadratic was the right shape when aWear leaked a mean 0.13 onto whole
	 * curved parts — squaring keeps a wash off. It is the wrong shape now that the
	 * channel is honest, because it also crushes the thing it exists to show: a
	 * 0.45-authored chamfer landed at a 3.6 % mix and no arris on the weapon carried
	 * a highlight. A smoothstep rejects the residue below 0.12 completely AND lets a
	 * real chamfer reach bare metal, which a square can never do. */
	diffuseColor.rgb = mix( diffuseColor.rgb, uWearColor,
		smoothstep( 0.12, 0.52, gunEdge ) );
	// Hands leave the coating dirtier as well as smoother.
	diffuseColor.rgb *= 1.0 - vTouch * uCavP.w;
	diffuseColor.rgb = mix( diffuseColor.rgb, uGrimeColor, vGrime * 0.88 );`)
      // roughness: remap the packed channel into this material's authored band
      .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>
	roughnessFactor = clamp( uRough.x + gunORM.r * uRough.y, 0.02, 1.0 );
	// ...then widen it over centimetres, not over texels. A per-texel roughness
	// field mips back to one gloss level; this one survives to the silhouette.
	roughnessFactor = clamp( roughnessFactor + gunMacro * uMacroRough * 2.0, 0.02, 1.0 );
	roughnessFactor = clamp( roughnessFactor + gunObj * uMacroRough * 2.6, 0.02, 1.0 );
	/* THE ROUGHNESS FIELD, and it has a cause.
	 * Burnish where the weapon is held (aTouch), matt where dust has settled in a
	 * recess. Those are two independent spatial fields on one surface, which is
	 * the thing FINDINGS F20 says every shipped material has and none of ours did. */
	roughnessFactor = clamp( roughnessFactor - vTouch * uCavP.z, 0.02, 1.0 );
	roughnessFactor = clamp( roughnessFactor + gunCav * uCavP.y, 0.02, 1.0 );
	roughnessFactor = mix( roughnessFactor, uWearRough, gunEdge );
	roughnessFactor = mix( roughnessFactor, min( 0.96, roughnessFactor + 0.20 ), vGrime );`)
      // metalness: coatings are dielectric; only worn-through metal is not
      .replace('#include <metalnessmap_fragment>', `#include <metalnessmap_fragment>
	// ASSIGNED, not multiplied. The scalar material.metalness is now the honest
	// physically-authored value of the surface, so a raw .clone() that loses this
	// injection falls back to something true instead of to bare metal — the same
	// trap that made a wooden trunk render as chrome in the world library.
	metalnessFactor = gunORM.g * uMetalScale;
	metalnessFactor = mix( metalnessFactor, 1.0, gunEdge );
	// Dust in a recess is a dielectric powder over whatever is underneath.
	metalnessFactor = clamp( metalnessFactor * ( 1.0 - gunCav * 0.45 ), 0.0, 1.0 );
	metalnessFactor = clamp( metalnessFactor * ( 1.0 - vGrime * 0.55 ), 0.0, 1.0 );`)
      // cavity + baked AO on the indirect terms only, so the key light still reads
      .replace('#include <aomap_fragment>', `#include <aomap_fragment>
	{
		float gunAO = mix( 1.0, gunORM.b * vAO, uAOStrength );
		reflectedLight.indirectDiffuse *= gunAO;
		// A metal's appearance IS the environment, so the environment it cannot
		// see must not reach it. Without this the bolt carrier down inside the
		// port, the underside of the charging handle and its exposed top flat all
		// return exactly the same sky — a bare-metal part with no self-occlusion
		// on its INDIRECT SPECULAR is a mirror, and a mirror is what reads as
		// chrome. This costs nothing: \`vSky\` is already baked and already
		// interpolated for the direct term below.
		reflectedLight.indirectSpecular *= gunAO * mix( 1.0, vSky, uSkySpec );
		// Baked micro-shadowing on the DIRECT terms. \`vSky\` is how much of the
		// weapon's own upper hemisphere this vertex can see, so the rail under the
		// optic, the inside of every Picatinny slot and the magwell throat stop
		// sitting at exactly the same value as the surfaces around them.
		float micro = mix( 1.0, vSky, uSkyShadow ) * mix( 1.0, gunAO, 0.30 );
		reflectedLight.directDiffuse *= micro;
		reflectedLight.directSpecular *= micro;
	}`);
  };
  mat.customProgramCacheKey = () => 'gunSurface4';
  return mat;
}

/* ------------------------------------------------------------------ *
 * Public
 * ------------------------------------------------------------------ */

export class GunMaterialLibrary {
  constructor(ctx) {
    this.ctx = ctx;
    this.map = new Map();
    this.tex = buildTextures();
    const marks = buildMarkAtlas();
    this.markTex = marks.tex;
    this.markNrm = marks.nrm;
    this.reticleTex = buildReticle();
    this.env = null;
    this._build();
    this._auditPalette();
  }

  /**
   * Take the shared material as a hint.
   *
   * Kept for the cloth/webbing families. It is deliberately NOT used for the
   * eight structural families: `_hint` only adopts a `normalMap` when the gun
   * material has none of its own, which is never — so calling it on a `gun_*`
   * name would do nothing except make `Materials.mapsFor()` generate a full
   * 512 map set out of a texture budget that is already at 592 MB of 640.
   * What is consumed from the library is its *authoring*; see FAMILY.
   */
  _hint(name) {
    try {
      const m = this.ctx.materials?.get?.(name);
      return m && m.color ? { mat: m } : null;
    } catch { return null; }
  }

  /**
   * Assert that every family still names a material the library actually
   * publishes. This reads `ctx.materials.names` only, so it costs nothing and
   * generates nothing — but if the materials team renames or drops one of the
   * `gun_*` surfaces, the derivation stops being traceable and we say so
   * instead of quietly drifting back to one flat colour.
   */
  _auditPalette() {
    try {
      const names = this.ctx.materials?.names;
      if (!Array.isArray(names)) return;
      const missing = [];
      for (const [key, f] of Object.entries(FAMILY)) {
        if (f.src.startsWith('(')) continue;      // not derived from the library
        if (!names.includes(f.src)) missing.push(`${key} <- ${f.src}`);
      }
      if (missing.length) {
        console.warn('[weapons] palette source(s) no longer in ctx.materials:', missing.join(', '));
      }
    } catch { /* the library is optional at boot */ }
  }

  _phys(name, params, hintName) {
    const hint = hintName ? this._hint(hintName) : null;
    const m = new THREE.MeshPhysicalMaterial(params);
    // three.js OVERWRITES `envMapIntensity` with `scene.environmentIntensity` for
    // any standard material whose own `envMap` is null (WebGLRenderer.setProgram).
    // So every per-material IBL weight authored here was silently discarded and
    // every surface on the gun reflected the sky at one shared strength. The
    // authored weight is kept here and re-applied in `bindEnvironment`.
    m.userData.iblWeight = params.envMapIntensity ?? 1;
    if (hint?.mat?.normalMap && !m.normalMap) m.normalMap = hint.mat.normalMap;
    m.name = `wpn_${name}`;
    this.map.set(name, m);
    return m;
  }

  _build() {
    const T = this.tex;

    /* ================================================================ *
     * The differentiated structural set.
     *
     * Every family below is one entry of `FAMILY`, i.e. one of the
     * materials team's named `gun_*` surfaces re-derived at viewmodel texel
     * density. The physics the previous iteration established is kept
     * verbatim and is the reason this reads as different MATERIALS rather
     * than as different paint:
     *
     *  - phosphate / anodising / Cerakote are conversion coatings, i.e.
     *    DIELECTRICS. `metalScale` is a fraction, not 1. Only `bare` is a
     *    true metal, and only worn-through edges (`vWear`) reach it.
     *  - one packed ORM sampler, so every material binds exactly four
     *    texture units — map, normalMap, uORM, envMap (FINDINGS F6).
     *  - `envMapIntensity` is authored into `userData.iblWeight` and
     *    re-applied in `bindEnvironment()` against an explicitly bound
     *    `material.envMap`, because three.js silently overwrites the
     *    property for any material whose own envMap is null.
     * ================================================================ */

    const structural = (key, extra = {}) => {
      const f = FAMILY[key];
      const maps = T.family[key];
      const isMetal = f.metalScale >= 0.99;
      return applySurface(this._phys(key, {
        color: 0xffffff,                       // the hue lives in the albedo map
        map: maps.albedo,
        // `roughness`/`metalness` are the honest fallbacks if the injection is
        // ever lost by a raw clone; the ORM map wins whenever it is live.
        roughness: (f.rough[0] + f.rough[1]) * 0.5,
        metalness: f.metalScale,
        normalMap: maps.normal,
        normalScale: new THREE.Vector2(f.nrm, f.nrm),
        specularIntensity: f.spec,
        envMapIntensity: f.ibl,
        ...(extra.mat || {}),
      }, null), {
        orm: maps.orm, roughMin: f.rough[0], roughMax: f.rough[1],
        metalScale: f.metalScale,
        // Worn-through edges go to BARE STEEL: bright, and — at wearRough —
        // sharp enough to actually catch the sun. This is the "worn-through
        // edges" half of the bare-metal fix, and it is why every chamfer on
        // the weapon now returns a highlight instead of a value step.
        // Warm-neutral, not the cool 0xd0d6de/0xc0c6ce it used to be: a cool
        // bright edge on a dark receiver is the nickel-plated tell, and every
        // chamfer on the weapon carries this colour.
        wearColor: isMetal ? 0xc9c2b6 : 0xb9b2a6,
        wearRough: isMetal ? 0.090 : 0.105,
        // Bare metal gets the strongest macro modulation of anything on the
        // weapon: it is the only surface whose appearance IS the environment,
        // so it is the only one a single gloss level turns into a mirror.
        macroTone: f.macro ? f.macro[0] : 0.09,
        macroRough: f.macro ? f.macro[1] : 0.07,
        skySpec: isMetal ? 0.62 : 0.30,
        ao: extra.ao ?? 0.90,
        /* --- the iteration-5 fields (see the applySurface header) --------
         * A machinist leaves 0.4-1.0 mm of chamfer on an aluminium receiver and
         * the lathed parts carry 1-3 mm fillets, so 2.5 mm is the radius above
         * which nothing counts as an edge and 0.6 mm is where wear is total.
         * Authoring it as a radius rather than as a curvature is the difference
         * between a number somebody can check against a real part and a number
         * fitted to a screenshot (FINDINGS F15). */
        edgeWear: 0.90, edgeRadius: 0.0025, edgeRadiusFull: 0.0006, edgePatch: 0.40,
        /* Cavity dirt. Strongest on the coatings, which is where dust actually
         * clings; bare metal is oiled and sheds it, polymer holds a little. */
        cavityDirt: isMetal ? 0.26 : 0.46,
        cavityRough: isMetal ? 0.10 : 0.18,
        cavityColor: 0x0e0d0b,
        // Burnish where the weapon is held. Bare metal polishes hardest.
        touchPolish: isMetal ? 0.16 : 0.13,
        touchDirt: isMetal ? 0.06 : 0.12,
        // Object-space tone field, ~7.5 cm — a whole handguard is two cycles of
        // it, which is the scale at which a real part looks unevenly finished.
        objMacroMetres: 0.075,
        objMacroTone: isMetal ? 0.16 : 0.11,
        ...(extra.surf || {}),
      });
    };

    /* Type-III anodised 7075 — the UPPER RECEIVER. Olive-grey, the warm end
     * of the palette (`gun_alloy`, the materials team measured it at 44.7 deg
     * against the optic housing's 22.5). */
    structural('alloy', {
      mat: { anisotropy: 0.30, anisotropyRotation: Math.PI * 0.5 },
    });

    /* Hard-anodised aluminium, graphite — LOWER RECEIVER, MAGWELL, RAILS,
     * controls, buffer tube. Deliberately a different anodising lot from the
     * upper: a two-tone upper/lower is what a real service rifle looks like,
     * and it is what stops the receiver reading as one moulded lump.
     * (`gun_metal`, cool ~213 deg base hue.) */
    structural('metal', {
      mat: { anisotropy: 0.45, anisotropyRotation: Math.PI * 0.5 },
    });

    /* Black hard anodise, satin — the OPTIC HOUSING. Bead-blasted, not
     * broached, and darker than either receiver. (`gun_optic`.) */
    structural('optic', {
      mat: { anisotropy: 0.22, anisotropyRotation: Math.PI * 0.5 },
    });

    /* Manganese phosphate — small steel parts, and the structural finish on
     * the shotgun / pistol / SMG. Warm-neutral, the roughest coating here. */
    structural('steel', { ao: 0.90 });

    /* BARE STEEL. Barrel, bolt carrier, charging handle, gas key, extractor,
     * pins, sling swivels, trigger, muzzle crown. Roughness 0.075-0.19 and
     * metalness 1: the only surface on the weapon whose appearance IS the
     * environment, and the reason `wpn_closeup` has a specular return at all.
     * (`gun_steel_bare`, authored at roughness 0.085.) */
    structural('bare', {
      ao: 0.80,
      mat: { anisotropy: 0.62, anisotropyRotation: Math.PI * 0.5 },
    });

    /* Glass-filled nylon, warm brown-black — MAGAZINE and pistol grip.
     * The clearcoat lobe is what a moulded part has and a machined one does
     * not: a thin release-agent skin over a diffuse body. (`gun_polymer`.) */
    structural('polymer', {
      ao: 0.95,
      mat: { clearcoat: 0.16, clearcoatRoughness: 0.55 },
    });

    /* FDE furniture — the HANDGUARD, and the shotgun / SMG furniture.
     * (`gun_polymer_fde`, warm tan ~33 deg.) */
    structural('polymer_fde', {
      ao: 0.95,
      mat: { clearcoat: 0.10, clearcoatRoughness: 0.62 },
    });

    /* Wolf grey furniture — the STOCK. Cool, so a two-tone build still
     * separates in hue from the tan handguard. (`gun_polymer_grey`.) */
    structural('polymer_grey', {
      ao: 0.95,
      mat: { clearcoat: 0.12, clearcoatRoughness: 0.58 },
    });

    // `bright` is the old name for bare metal; Guns2 still calls it that.
    this.map.set('bright', this.map.get('bare'));

    // Rubber: buttpad, grip overmould, optic bikini cover, optic liner.
    applySurface(this._phys('rubber', {
      color: 0xffffff,
      map: T.rubberAlbedo,
      roughness: 0.92,
      metalness: 0.0,
      normalMap: T.polyNormal,
      normalScale: new THREE.Vector2(0.60, 0.60),
      specularIntensity: 0.20,
      envMapIntensity: 0.45,
    }), {
      orm: T.rubberORM, roughMin: 0.860, roughMax: 0.985, metalScale: 0.0,
      wearColor: 0x2a2c2e, wearRough: 0.70, ao: 0.95,
      // Rubber does not develop a bright arris — it goes shiny where it is gripped
      // and that is all. Edge wear off, contact burnish on hard.
      edgeWear: 0.0, touchPolish: 0.30, touchDirt: 0.08,
      cavityDirt: 0.40, cavityRough: 0.06, objMacroTone: 0.10,
    });

    // Brass: loaded rounds visible at the top of the magazine, ejected cases.
    applySurface(this._phys('brass', {
      color: 0xffffff,
      map: T.brassAlbedo,
      roughness: 0.30,
      metalness: 1.0,
      normalMap: T.bareNormal,
      normalScale: new THREE.Vector2(0.22, 0.22),
      envMapIntensity: 0.85,
    }), {
      orm: T.brassORM, roughMin: 0.200, roughMax: 0.360, metalScale: 1.0,
      wearColor: 0xd8b46a, wearRough: 0.16, ao: 0.85,
      // A case mouth and a rim are the two places brass is bright; the body of a
      // loaded round in a magazine is tarnished and slightly dirty.
      edgeWear: 0.80, edgeRadius: 0.0012, edgeRadiusFull: 0.0004,
      cavityDirt: 0.30, cavityRough: 0.12, cavityColor: 0x161009,
      touchPolish: 0.10, objMacroTone: 0.12,
    });

    // Walnut furniture for the shotgun.
    applySurface(this._phys('wood', {
      color: 0xffffff,
      map: T.woodAlbedo,
      roughness: 0.38,
      metalness: 0.0,
      clearcoat: 0.34,
      clearcoatRoughness: 0.24,
      normalMap: T.polyNormal,
      normalScale: new THREE.Vector2(0.30, 0.30),
      envMapIntensity: 0.60,
    }), {
      orm: T.woodORM, roughMin: 0.290, roughMax: 0.440, metalScale: 0.0,
      wearColor: 0x6a4326, wearRough: 0.22, ao: 0.95,
      // Walnut burnishes rather than chips: the arris goes darker and glossier.
      edgeWear: 0.55, edgeRadius: 0.0035, edgeRadiusFull: 0.0010,
      cavityDirt: 0.34, cavityRough: 0.10, cavityColor: 0x120a05,
      touchPolish: 0.16, touchDirt: 0.08, objMacroTone: 0.14,
    });

    /* ---------------- gloves and sleeve --------------------------------
     * The hands must read as a *separate object* from the weapon, so the glove
     * is authored ~45% darker than the receiver and at nearly double its
     * roughness. Value separation is what makes a hand read as a hand and not
     * as another chunk of the gun.
     */
    applySurface(this._phys('glove', {
      color: 0xffffff,
      map: T.clothAlbedo,
      roughness: 0.90,
      metalness: 0.0,
      normalMap: T.clothNormal,
      normalScale: new THREE.Vector2(0.95, 0.95),
      specularIntensity: 0.15,
      sheen: 0.10,
      sheenRoughness: 0.9,
      sheenColor: new THREE.Color(0x24272c),
      envMapIntensity: 0.55,
    }, 'cloth_camo'), {
      orm: T.clothORM, roughMin: 0.830, roughMax: 0.960, metalScale: 0.0,
      wearColor: 0x24272b, wearRough: 0.70, ao: 1.0,
      /* CLOTH MUST NOT TAKE THE EDGE-WEAR LAYER. `gunEdge` drives metalness to 1
       * where it fires, which on a knuckle fillet would be a chrome finger. A glove
       * gets its variation from contact and from dirt in the seams instead — which
       * is what it gets in reality: shiny across the palm and the first knuckles,
       * dark and matt in every stitch line. */
      edgeWear: 0.0, touchPolish: 0.16, touchDirt: 0.06,
      cavityDirt: 0.36, cavityRough: 0.04, cavityColor: 0x0b0b0c,
      objMacroMetres: 0.045, objMacroTone: 0.13,
    });

    // Camo sleeve.
    applySurface(this._phys('sleeve', {
      color: 0xffffff,
      roughness: 0.90,
      metalness: 0.0,
      map: T.camo,
      normalMap: T.clothNormal,
      normalScale: new THREE.Vector2(1.0, 1.0),
      specularIntensity: 0.15,
      sheen: 0.05,
      sheenRoughness: 0.92,
      sheenColor: new THREE.Color(0x2b2d26),
      envMapIntensity: 0.45,
    }, 'cloth_camo'), {
      orm: T.clothORM, roughMin: 0.860, roughMax: 0.975, metalScale: 0.0,
      wearColor: 0x3b3d33, wearRough: 0.78, ao: 1.0,
      edgeWear: 0.0, touchPolish: 0.10, touchDirt: 0.04,
      cavityDirt: 0.30, cavityRough: 0.03, cavityColor: 0x0c0c0a,
      objMacroMetres: 0.060, objMacroTone: 0.12,
    });

    // Webbing / cuff strap / palm reinforcement.
    applySurface(this._phys('webbing', {
      color: 0xffffff,
      map: T.webAlbedo,
      roughness: 0.94,
      metalness: 0.0,
      normalMap: T.clothNormal,
      normalScale: new THREE.Vector2(1.35, 1.35),
      specularIntensity: 0.13,
      envMapIntensity: 0.45,
    }, 'webbing'), {
      orm: T.webORM, roughMin: 0.880, roughMax: 0.990, metalScale: 0.0,
      wearColor: 0x2c2c26, wearRough: 0.80, ao: 1.0,
      edgeWear: 0.0, touchPolish: 0.12, touchDirt: 0.05,
      cavityDirt: 0.34, cavityRough: 0.03, cavityColor: 0x0a0a08,
      objMacroMetres: 0.040, objMacroTone: 0.14,
    });

    /* ---------------- roll marks ---------------------------------------
     *
     * "Floating '5.56 NATO' decal" — F20's verdict on `wpn_closeup`, and it was
     * exactly right. Three things made it float and all three are fixed here.
     *
     * 1. VALUE. 0xa9a49a is linear 0.39 against a receiver coating at linear
     *    0.015 — twenty-five times brighter. A laser etch removes the dye from
     *    the top few microns of an anodised layer; it goes a LITTLE lighter and
     *    a lot matter. It is a mark on the part, not a sticker on it.
     * 2. NO GROOVE. The mark had a normal map and nothing to make the groove
     *    WALL darker, so it read as ink lying on a flat. The alpha ramp between
     *    alphaTest and 1.0 IS the wall — the anti-aliased shoulder of every
     *    stroke — so darkening in that band and roughening it gives the stroke
     *    a shadowed edge, which is the whole of what says "cut in" to the eye.
     * 3. NO ENVIRONMENT DIFFERENCE. It reflected the sky exactly as hard as the
     *    surface it sits on, so it never separated by finish either.
     */
    const marks = new THREE.MeshPhysicalMaterial({
      name: 'wpn_marks',
      color: 0x6d675e,
      map: this.markTex,
      normalMap: this.markNrm,
      normalScale: new THREE.Vector2(2.4, 2.4),
      transparent: true,
      alphaTest: 0.34,
      roughness: 0.80,
      metalness: 0.04,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
      envMapIntensity: 0.34,
      side: THREE.DoubleSide,
    });
    marks.alphaMap = this.markTex;
    marks.userData.iblWeight = 0.34;
    marks.onBeforeCompile = (shader) => {
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <alphatest_fragment>', `#include <alphatest_fragment>
	{
		// The alpha shoulder is the engraved wall: dark and rough at the rim of
		// every stroke, cleaner in the middle of it.
		float etchWall = 1.0 - smoothstep( 0.34, 0.86, diffuseColor.a );
		diffuseColor.rgb *= mix( 1.0, 0.30, etchWall );
		gEtchWall = etchWall;
	}`)
        .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>
	roughnessFactor = clamp( roughnessFactor + gEtchWall * 0.18, 0.02, 1.0 );`)
        .replace('void main() {', 'float gEtchWall;\nvoid main() {');
    };
    marks.customProgramCacheKey = () => 'gunMarks2';
    this.map.set('marks', marks);

    // Optic lens: anti-reflective coated glass with the classic ruby cast.
    const lens = new THREE.MeshPhysicalMaterial({
      name: 'wpn_lens',
      color: 0x0d1a24,
      roughness: 0.030,
      metalness: 0.0,
      transmission: 0.0,
      transparent: true,
      opacity: 0.19,
      ior: 1.52,
      reflectivity: 0.9,
      clearcoat: 1.0,
      clearcoatRoughness: 0.02,
      envMapIntensity: 1.6,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    this.map.set('lens', lens);

    // Emissive red dot, additive so it survives any exposure.
    // The dot is additive against a sunlit street, so it is authored above 1.0 —
    // `toneMapped: false` keeps it out of the exposure curve, which is what makes a
    // 2 MOA dot read at any time of day.
    const reticle = new THREE.MeshBasicMaterial({
      name: 'wpn_reticle',
      map: this.reticleTex,
      color: 0xff2a12,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
      toneMapped: false,
      side: THREE.DoubleSide,
    });
    reticle.color.multiplyScalar(3.2);
    this.map.set('reticle', reticle);
  }

  get(key) {
    return this.map.get(key) || this.map.get('metal');
  }

  /**
   * Bind the render team's viewmodel environment onto every gun material.
   *
   * Assigning `material.envMap` explicitly is what stops three from clobbering
   * `envMapIntensity`; the *absolute* level still comes from the render team, since
   * we multiply our authored per-material weight by their
   * `viewScene.environmentIntensity` (CONTRACTS.md :: "The viewmodel is on the same
   * scale"). Without this a matte polymer handguard reflected the sky exactly as
   * hard as a satin anodised rail.
   */
  bindEnvironment(viewScene) {
    const env = viewScene.environment || this.env || null;
    const scale = viewScene.environmentIntensity ?? 1;
    const changed = env !== this._boundEnv;
    this._boundEnv = env;
    for (const m of this.map.values()) {
      if (m.isMeshBasicMaterial) continue;
      if (changed) { m.envMap = env; m.needsUpdate = true; }
      const w = m.userData.iblWeight;
      if (w !== undefined) m.envMapIntensity = w * scale;
    }
  }

  /** Every material that consumes the ORM/wear/grime vertex channels. */
  get surfaceMaterials() {
    const out = [];
    for (const m of this.map.values()) if (m.userData.roughBand) out.push(m);
    return out;
  }

  /**
   * Fallback IBL only.
   *
   * `ProbeRig.syncViewmodelLights` assigns `ctx.viewScene.environment` from
   * `ctx.scene.environment` every frame at render priority 1000 — that is the
   * documented contract (CONTRACTS.md :: "The viewmodel is on the same scale"),
   * and we do not fight it. This studio rig exists only so the metals have
   * something to reflect during boot, before the world PMREM exists.
   */
  installEnvironment(renderer, viewScene) {
    if (viewScene.environment) { this.env = viewScene.environment; return; }
    try {
      const pmrem = new THREE.PMREMGenerator(renderer);
      const rig = new THREE.Scene();
      const panel = (c, i, w, h, pos, look) => {
        const m = new THREE.Mesh(
          new THREE.PlaneGeometry(w, h),
          new THREE.MeshBasicMaterial({ color: c, side: THREE.DoubleSide }),
        );
        m.material.color.multiplyScalar(i);
        m.position.set(pos[0], pos[1], pos[2]);
        m.lookAt(look[0], look[1], look[2]);
        rig.add(m);
        return m;
      };
      rig.background = new THREE.Color(0x0a0d12);
      panel(0x2c3f5a, 1.0, 40, 40, [0, 10, 0], [0, 0, 0]);
      panel(0x2a231a, 1.0, 40, 40, [0, -10, 0], [0, 0, 0]);
      panel(0xfff0d8, 4.2, 5, 4, [-7, 6, 5], [0, 0, 0]);
      panel(0x8fb2dd, 0.55, 14, 10, [8, 1, -4], [0, 0, 0]);
      panel(0x14171c, 1.0, 30, 20, [0, 0, 12], [0, 0, 0]);
      const rt = pmrem.fromScene(rig, 0.06);
      this.env = rt.texture;
      viewScene.environment = rt.texture;
      viewScene.environmentIntensity = 0.85;
      rig.traverse((o) => { o.geometry?.dispose(); o.material?.dispose(); });
      pmrem.dispose();
    } catch (e) {
      console.warn('[weapons] environment generation failed:', e);
    }
  }

  dispose() {
    for (const m of this.map.values()) m.dispose();
    for (const t of Object.values(this.tex)) t.dispose?.();
    this.markTex.dispose();
    this.markNrm.dispose();
    this.reticleTex.dispose();
  }
}

export { applySurface };
